import classNames from 'classnames';
import { Slot } from 'radix-ui';
import { Children, type ReactNode, useId } from 'react';
import { Flex } from '../Flex';
import { Skeleton } from '../Skeleton';
import type { SharedComponentProps } from '../types';
import * as styles from './index.css';

type RootProps = SharedComponentProps<'form'>;

/**
 * A form root element. Simply renders a `<form>` element.
 */
function Root({ children, className, ...props }: RootProps) {
    return (
        <form className={className} noValidate {...props}>
            {children}
        </form>
    );
}

type ContentProps = SharedComponentProps<'div'>;

/**
 * A container for form content. Use this to wrap multiple `<Form.Section>` elements.
 * You may omit this if you only have one section.
 */
function Content({ children, className, ...props }: ContentProps) {
    return (
        <div className={classNames(styles.content, className)} {...props}>
            {children}
        </div>
    );
}

type SectionProps = SharedComponentProps<'fieldset'> & {
    title?: string;
};

/**
 * A single thematic section of a form, with an optional title.
 * Use by itself, or within a `<Form.Content>` element if you have multiple sections.
 */
function Section({ children, className, title, ...props }: SectionProps) {
    return (
        <fieldset className={classNames(styles.section.root, className)} {...props}>
            {title && <legend className={styles.section.title}>{title}</legend>}
            {children}
        </fieldset>
    );
}

type RowProps = SharedComponentProps<'fieldset'> & {
    title?: string;
};

/**
 * A row with multiple `<Form.Field>` elements, and an optional title.
 * The fields are rendered with flex 1 by default, so they will evenly distribute the available space.
 */
function Row({ children, className, title, ...props }: RowProps) {
    return (
        <fieldset className={className} {...props}>
            {title && <legend className={styles.row.title}>{title}</legend>}
            <div className={styles.row.content}>
                {Children.map(children, (child) => (
                    <Slot.Root className={styles.row.item}>{child}</Slot.Root>
                ))}
            </div>
        </fieldset>
    );
}

type FieldProps = SharedComponentProps<'div'> & {
    id?: string;
    label?: string;
    required?: boolean;
    error?: string;
    control: (props: { id: string; required?: boolean }) => ReactNode;
};

/**
 * A single form field.
 * @param id The ID of the field.
 * @param label The label of the field.
 * @param required Whether the field is required.
 * @param error The error message for the field.
 * @param control A function that returns the control for the field.
 * @param className The class name for the field.
 * @param props The props for the field.
 * @returns A form field.
 *
 * @example
 * ```tsx
 * <FormField
 *   id="lastName"
 *   label="氏"
 *   required
 *   error={errors.lastName?.message}
 *   control={props => (
 *      <Input {...props} {...register('lastName')} placeholder="山田" />
 *   )}
 * />
 * ```
 */
function Field({ label, required, error, control, className, ...props }: FieldProps) {
    const generatedId = useId();
    const id = props.id ?? generatedId;

    return (
        <div className={classNames(styles.field.root, className)} {...props}>
            {label && (
                <label htmlFor={id} className={styles.field.label}>
                    {label}
                    {required && <span className={styles.field.requiredMark}>＊</span>}
                </label>
            )}
            {control({ id, required })}
            {error && <span className={styles.field.error}>{error}</span>}
        </div>
    );
}

type FieldSkeletonProps = SharedComponentProps<'div'> & {
    /** Whether to leave room for a label. @default true */
    label?: boolean;
    /** Height of the control placeholder. Defaults to `Input`/`Select`'s own height. @default 42 */
    controlHeight?: number | string;
};

/**
 * A placeholder for a `<Form.Field>` that is still loading. Reuses the field's own
 * layout, so the real control drops in without shifting anything around it.
 */
function FieldSkeleton({ label = true, controlHeight = 42 }: FieldSkeletonProps) {
    return (
        <Flex className={styles.field.root}>
            {label && <Skeleton.Line width={128} height="sm" />}
            <Skeleton.Box height={controlHeight} />
        </Flex>
    );
}

export const Form = { Root, Content, Section, Row, Field, FieldSkeleton };

export type {
    ContentProps as FormContentProps,
    FieldProps as FormFieldProps,
    FieldSkeletonProps as FormFieldSkeletonProps,
    RootProps as FormProps,
    RowProps as FormRowProps,
    SectionProps as FormSectionProps,
};
